// A row of numbered step chips: the active one solid blue, finished ones
// tinted, the rest neutral. Scrolls sideways on narrow screens instead of
// wrapping, so the chips keep reading as one timeline.
//
//   <StepTimeline steps={[{ id: "intro", label: "Intro" }, …]} active={2}
//     isDone={(i) => i < 2} onSelect={(i) => seek(i)} />
import cn from "classnames";

export interface TimelineStep {
  id: string;
  label: string;
}

interface Props {
  steps: TimelineStep[];
  active: number;
  /** Defaults to "every step before the active one". */
  isDone?: (index: number) => boolean;
  onSelect?: (index: number) => void;
  disabled?: boolean;
  className?: string;
}

const StepTimeline: React.FC<Props> = ({ steps, active, isDone, onSelect, disabled, className }) => {
  const done = isDone ?? ((i: number) => i < active);
  return (
    <ol className={cn("flex items-center gap-2 overflow-x-auto scrollbar-hide py-1 -mx-1 px-1", className)} aria-label="Steps">
      {steps.map((s, i) => {
        const isActive = i === active;
        const isDoneStep = !isActive && done(i);
        return (
          <li key={s.id} className="shrink-0">
            <button
              type="button"
              disabled={disabled}
              aria-current={isActive ? "step" : undefined}
              onClick={() => onSelect?.(i)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border text-xs font-medium pl-1 pr-2.5 py-0.5 transition-colors whitespace-nowrap",
                "disabled:opacity-50 disabled:cursor-default",
                isActive
                  ? "bg-blue-500 border-blue-500 text-white"
                  : isDoneStep
                    ? "bg-blue-50 dark:bg-blue-900/40 border-blue-100 dark:border-blue-900/60 text-blue-600 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/60"
                    : "bg-base-100 border-base-300 text-base-content/70 hover:bg-base-200",
              )}
            >
              <span
                className={cn(
                  "inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] tabular-nums",
                  isActive ? "bg-white/20 text-white" : isDoneStep ? "bg-blue-500 text-white" : "bg-base-200 text-base-content/60",
                )}
              >
                {i + 1}
              </span>
              {s.label}
            </button>
          </li>
        );
      })}
    </ol>
  );
};

export default StepTimeline;
